/* FILE: ./frontend/src/features/legal/PrivacyPolicy.jsx */
import React, { useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';

export default function PrivacyPolicy() {
  const { t } = useTranslation(); 

  // ⚡ SEO FIX: Forces Google to hide this page from search results
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = "robots";
    meta.content = "noindex, follow";
    document.head.appendChild(meta);

    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  return (
    <div className="max-w-4xl mx-auto py-12 px-6 bg-white shadow-sm mt-8 rounded-xl min-h-screen">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">{t('legal.privacy_title', 'Privacy Policy')}</h1>
      <p className="text-sm text-gray-500 mb-8">{t('legal.last_updated', 'Last Updated: August 2026')}</p>
      
      <div className="prose prose-blue max-w-none space-y-6 text-gray-700">
        
        <section>
          <h2 className="text-xl font-bold text-gray-900">{t('legal.p1_title', '1. Information We Collect')}</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li><Trans i18nKey="legal.p1_l1"><strong>Business Identity:</strong> Legal Name, Business Number (BN9), Incorporation Date, and Address.</Trans></li>
            <li><Trans i18nKey="legal.p1_l2"><strong>Contact Information:</strong> Representative names, email addresses, and phone numbers.</Trans></li>
            <li><Trans i18nKey="legal.p1_l3"><strong>Transaction Data:</strong> Details of Requests for Quotation (RFQs), Quotes issued, and messages exchanged.</Trans></li>
            <li><Trans i18nKey="legal.p1_l4"><strong>Financial Data:</strong> Billing details and transaction history. (Note: Credit card information is tokenized and processed directly by our secure third-party payment gateway, Helcim. Tariffbeater Inc. does not store raw credit card numbers on our servers).</Trans></li>
            {/* ⚡ UPDATED CLAUSE: Now includes page views and session activity */}
            <li><Trans i18nKey="legal.p1_l5"><strong>Technical Data:</strong> IP addresses, browser types, cookies, login timestamps, page views, and internal session activity for security, audit logging, and platform analytics.</Trans></li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900">{t('legal.p2_title', '2. How We Use Your Information')}</h2>
          <p>{t('legal.p2_desc', 'We use the collected information to:')}</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>{t('legal.p2_l1', 'Verify business legitimacy and prevent fraud.')}</li>
            <li>{t('legal.p2_l2', 'Facilitate B2B connections and the transmission of RFQs.')}</li>
            <li>{t('legal.p2_l3', 'Process billing and manage subscriptions.')}</li>
            <li>{t('legal.p2_l4', 'Enforce compliance with our strictly civilian-use venue policy.')}</li>
            {/* ⚡ NEW CLAUSE: Establishes legitimate business purpose for analytics */}
            <li>{t('legal.p2_l5', 'Monitor system performance, analyze user journeys, and improve overall platform functionality.')}</li>
          </ul>
          <p className="mt-2"><Trans i18nKey="legal.p2_bold"><strong>We do not sell your personal data to third-party advertisers.</strong></Trans></p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900">{t('legal.p3_title', '3. Information Sharing')}</h2>
          <p>
            {t('legal.p3_desc', 'We may share your data with trusted third parties strictly to operate our platform:')}
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>
                <Trans i18nKey="legal.p3_l1"><strong>Government Business Registries:</strong> We transmit your Business Number and corporate details to relevant Federal, Provincial, or Territorial registries to validate your business's legal status and active standing.</Trans>
            </li>
            <li>
                <Trans i18nKey="legal.p3_l2"><strong>Payment Processors:</strong> Transaction details are shared with our payment gateway to facilitate secure subscription billing.</Trans>
            </li>
            <li>
                <Trans i18nKey="legal.p3_l3"><strong>Law Enforcement & Regulatory Authorities:</strong> We may disclose information if required by valid legal process (e.g., court order or subpoena), or to enforce our Terms of Service regarding the investigation of prohibited items.</Trans>
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900">{t('legal.p4_title', '4. Communications')}</h2>
          <p>
            <Trans i18nKey="legal.p4_desc">By creating an account or sending an RFQ, you imply consent to receive business-to-business (B2B) communications relevant to that transaction. 
            We comply with <strong>Canada’s Anti-Spam Legislation (CASL)</strong>. You may unsubscribe from marketing emails at any time, 
            but transactional notifications (e.g., "New Quote Received", "Subscription Renewal") are mandatory for active users.</Trans>
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900">{t('legal.p5_title', '5. Data Security and Retention')}</h2>
          <p>
            <Trans i18nKey="legal.p5_sec"><strong>Security:</strong> We use industry-standard encryption (SSL/TLS) for data in transit and at rest.</Trans>
          </p>
          <p className="mt-2">
            <Trans i18nKey="legal.p5_ret"><strong>Retention:</strong> Transaction records are retained for a minimum of <strong>7 years</strong> to align with Canada Revenue Agency (CRA) 
            requirements for business records. Even if you delete your account, historical transaction logs involving other parties may be archived securely for tax purposes.</Trans>
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900">{t('legal.p6_title', '6. User Rights')}</h2>
          <p>
            {t('legal.p6_desc', 'You have the right to request access to, correction of, or deletion of your personal data. To exercise these rights, please contact our support team. Note that data deletion requests may be subject to legal retention obligations.')}
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900">{t('legal.p7_title', '7. Bot Protection & Automated Security')}</h2>
          <p>
            <Trans i18nKey="legal.p7_desc">To maintain the integrity of our B2B marketplace and protect our systems from automated abuse, we use Cloudflare Turnstile. Turnstile is an invisible security tool that verifies human visitors in the background without requiring visual puzzles. Turnstile analyzes browser data and behavioral signals to determine if a visitor is legitimate. Your use of this website and our registration forms is subject to the <a href="https://www.cloudflare.com/website-terms/" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Cloudflare Website Terms of Use</a> and <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Cloudflare Privacy Policy</a>.</Trans>
          </p>
        </section>

      </div>
    </div>
  );
}